import { useEffect, useRef, useState } from "react";
import {
  applySipClient,
  createAdminComplex,
  deleteSipClient,
  fetchSipClientOptions,
  fetchSipClientDirectory,
  SIP_CLIENT_DIRECTORY_CHANGED_EVENT,
  patchAdminComplex,
  saveSipClient,
  setSipClientStatus,
  stopCurrentSipClient,
} from "../../admin/api";
import PricesEditor, {
  pricesToSections,
  sectionsToPrices,
} from "./PricesEditor";
import "./ComplexEditForm.css";
import { useSessionSipActivity } from "./sessionActivity/SessionSipActivityContext";
import { useAdminNotifications } from "./notifications/AdminNotificationsContext";
import { useCities } from "../../providers/CitiesContext";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

const MAIN_FIELD_NAMES = new Set(["city", "name"]);
const WIDE_FIELD_NAMES = new Set([
  "type",
  "sip_number",
  "info",
  "completion_dates",
  "finishes_available",
  "coordinates",
]);

const CATEGORY_OPTIONS = [
  { label: "Жилой комплекс", value: "residential" },
  { label: "Коммерция", value: "commercial" },
  { label: "Авто", value: "vehicles" },
  { label: "Другое", value: "special" },
];

const FIELDS_BY_CATEGORY = {
  residential: [
    { name: "city", label: "Город", type: "text" },

    { name: "name", label: "Название", type: "text" },
    {
      name: "completion_dates",
      label: "Сроки сдачи",
      type: "textarea",
      rows: 4,
      hint: "По одному значению на строку",
    },
    { name: "developer", label: "Застройщик", type: "text" },
    { name: "location", label: "Расположение", type: "text" },
    { name: "metro", label: "Метро", type: "text" },
    { name: "isApartments", label: "Апартаменты", type: "checkbox" },
    {
      name: "finishes_available",
      label: "Отделка",
      type: "textarea",
      rows: 4,
      hint: "По одному значению на строку",
    },
    { name: "working_hours", label: "Время работы", type: "text" },
    {
      name: "cityCategory",
      label: "Категория города",
      type: "radio",
      options: [
        { label: "Город", value: "city" },
        { label: "Область", value: "region" },
      ],
    },
    { name: "coordinates", label: "Координаты", type: "coordinates" },
    { name: "presetBase", label: "Базовый preset маркера", type: "text" },
  ],

  commercial: [
    { name: "city", label: "Город", type: "text" },

    { name: "name", label: "Название", type: "text" },
    {
      name: "completion_dates",
      label: "Сроки сдачи",
      type: "textarea",
      rows: 4,
      hint: "По одному значению на строку",
    },
    { name: "developer", label: "Застройщик", type: "text" },
    { name: "location", label: "Расположение", type: "text" },
    { name: "info", label: "Информация", type: "textarea", rows: 6 },
    { name: "working_hours", label: "Время работы", type: "text" },
    {
      name: "cityCategory",
      label: "Категория города",
      type: "radio",
      options: [
        { label: "Город", value: "city" },
        { label: "Область", value: "region" },
      ],
    },
    { name: "coordinates", label: "Координаты", type: "coordinates" },
    { name: "presetBase", label: "Базовый preset маркера", type: "text" },
  ],

  vehicles: [
    { name: "city", label: "Город", type: "text" },

    { name: "name", label: "Название", type: "text" },
    { name: "info", label: "Информация", type: "textarea", rows: 8 },
    { name: "coordinates", label: "Координаты", type: "coordinates" },
    { name: "presetBase", label: "Базовый preset маркера", type: "text" },
  ],

  special: [
    { name: "city", label: "Город", type: "text" },

    { name: "name", label: "Название", type: "text" },
    { name: "info", label: "Информация", type: "textarea", rows: 8 },
    { name: "coordinates", label: "Координаты", type: "coordinates" },
    { name: "presetBase", label: "Базовый preset маркера", type: "text" },
  ],
};

function normalizeBoolean(value) {
  if (typeof value === "boolean") return value;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }

  return Boolean(value);
}

function arrayToTextarea(value) {
  if (Array.isArray(value)) {
    return value.join("\n");
  }

  if (typeof value === "string") {
    return value;
  }

  return "";
}

function textareaToArray(value) {
  return value
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

function coordinatesToArray(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return [value[0] ?? "", value[1] ?? ""];
}

function coordinatesToPayload(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  const latText = String(value[0] ?? "").trim().replace(",", ".");
  const lonText = String(value[1] ?? "").trim().replace(",", ".");

  if (!latText && !lonText) {
    return [];
  }

  const lat = Number(latText);
  const lon = Number(lonText);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    throw new Error("Координаты должны быть числами.");
  }

  return [lat, lon];
}

function transferNumberToPayload(value) {
  const text = String(value ?? "").trim();

  if (!text) {
    return null;
  }

  const number = Number(text);

  if (!Number.isInteger(number)) {
    throw new Error("Номер перевода должен быть целым числом.");
  }

  return number;
}

function offerPriceToPayload(value) {
  const text = String(value ?? "").trim();
  if (!text) return null;
  const offerPrice = Number(text);
  if (!Number.isFinite(offerPrice) || offerPrice < 0) throw new Error("Цена оффера должна быть неотрицательным числом.");
  return offerPrice;
}

const PRESET_COLORS = [
  "darkOrange",
  "darkGreen",
  "darkBlue",
  "lightBlue",
  "night",
  "blue",
  "red",
  "green",
  "orange",
  "yellow",
  "violet",
  "pink",
  "brown",
  "black",
  "olive",
  "gray",
];
const PRESET_COLOR_PATTERN = new RegExp(`#(${PRESET_COLORS.join("|")})`);

function stripPresetColor(value) {
  return String(value || "").replace(PRESET_COLOR_PATTERN, "#");
}

function sipClientsToArray(value) {
  if (Array.isArray(value)) {
    return value.filter((item) => item && typeof item === "object");
  }

  if (value && typeof value === "object") {
    return Object.entries(value)
      .map(([key, client]) => ({
        ...(client && typeof client === "object" ? client : {}),
        clientName: client?.clientName || client?.label || client?.name || key,
      }))
      .filter((item) => item.clientName);
  }

  return [];
}

function createLocalSipClientId() {
  return `new:${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`}`;
}

function createSipDraft(clientName, client, clientId = null) {
  const embeddedId = client?._id ? String(client._id) : "";
  return {
    _id: embeddedId,
    draftKey: embeddedId || createLocalSipClientId(),
    clientId: String(client?.clientId || clientId || ""),
    clientName: clientName || client?.clientName || "",
    status: typeof client?.status === "undefined" ? true : normalizeBoolean(client.status),
    sip_number: client?.sip_number ?? "",
    type: client?.type ?? "",
    transfer_number: client?.transfer_number ?? "",
    offerPrice: client?.offerPrice ?? "",
  };
}

export function createSipClientDrafts(complex) {
  return sipClientsToArray(complex?.sipClients).map((client) =>
    createSipDraft(client.clientName, client)
  );
}

export function removeSipClientDraft(drafts, draftKey) {
  return drafts.filter((item) => item.draftKey !== draftKey);
}

function sipDraftSignature(client) {
  return JSON.stringify({
    clientId: String(client?.clientId || ""),
    status: normalizeBoolean(client?.status),
    sip_number: String(client?.sip_number ?? "").trim(),
    type: String(client?.type ?? "").trim(),
    transfer_number: String(client?.transfer_number ?? "").trim(),
    offerPrice: String(client?.offerPrice ?? "").trim(),
  });
}

function createSipDraftSignatures(drafts) {
  return Object.fromEntries(drafts.map((draft) => [draft.draftKey, sipDraftSignature(draft)]));
}

export function preserveSelectedEmbeddedSipClientId(currentSelectedId, drafts) {
  const selectedId = String(currentSelectedId || "");
  return drafts.some((client) => String(client?._id || "") === selectedId)
    ? selectedId
    : String(drafts[0]?._id || "");
}

function createInitialState(complex) {
  return {
    status: normalizeBoolean(complex?.status),
    name: complex?.name ?? "",
    city: complex?.city ?? "",
    type: complex?.type ?? "",
    sip_number: complex?.sip_number ?? "",
    transfer_number: complex?.transfer_number ?? "",
    developer: complex?.developer ?? "",
    location: complex?.location ?? "",
    metro: complex?.metro ?? "",
    working_hours: complex?.working_hours ?? "",
    info: complex?.info ?? "",
    category: complex?.category ?? "residential",


    isApartments: normalizeBoolean(complex?.isApartments),
    completion_dates: arrayToTextarea(complex?.completion_dates),
    finishes_available: arrayToTextarea(complex?.finishes_available),
    cityCategory: complex?.cityCategory ?? "city",
    coordinates: coordinatesToArray(complex?.coordinates),
    presetBase: complex?.presetBase ?? stripPresetColor(complex?.preset),
    prices: pricesToSections(complex?.prices),
  };
}

function ComplexEditForm({ complex, mode = complex?._id ? "edit" : "create", onClose, onSaved, focusEmbeddedSipClientId }) {
  const { cities } = useCities();
  const { recordChanges } = useSessionSipActivity();
  const { notify } = useAdminNotifications();
  const [formState, setFormState] = useState(() => createInitialState(complex));
  const [sipClientOptions, setSipClientOptions] = useState([]);
  const [sipClientDirectory, setSipClientDirectory] = useState([]);
  const [newSipClientId, setNewSipClientId] = useState("");
  const initialSipDrafts = createSipClientDrafts(complex);
  const [sipClientDrafts, setSipClientDrafts] = useState(initialSipDrafts);
  const [savedSipDraftSignatures, setSavedSipDraftSignatures] = useState(() => createSipDraftSignatures(initialSipDrafts));
  const [selectedSipClientId, setSelectedSipClientId] = useState(() =>
    initialSipDrafts[0]?.draftKey || ""
  );
  const [isAddSipClientOpen, setIsAddSipClientOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSipSaving, setIsSipSaving] = useState(false);
  const [error, setError] = useState("");
  const loadedComplexIdRef = useRef(complex?._id ? String(complex._id) : mode);

  useEffect(() => {
    const nextComplexId = complex?._id ? String(complex._id) : mode;
    const isSameComplex = loadedComplexIdRef.current === nextComplexId;
    loadedComplexIdRef.current = nextComplexId;
    if (!isSameComplex) setFormState(createInitialState(complex));
    const nextSipClientDrafts = createSipClientDrafts(complex);
    setSipClientDrafts(nextSipClientDrafts);
    setSavedSipDraftSignatures(createSipDraftSignatures(nextSipClientDrafts));
    setSelectedSipClientId((currentSelectedId) =>
      preserveSelectedEmbeddedSipClientId(currentSelectedId, nextSipClientDrafts)
    );
    if (!isSameComplex) {
      setNewSipClientId("");
      setIsAddSipClientOpen(false);
    }
    setError("");
    setIsSaving(false);
    setIsSipSaving(false);
  }, [complex, mode]);

  useEffect(() => {
    if (sipClientDrafts.length === 0) {
      if (selectedSipClientId) setSelectedSipClientId("");
      return;
    }

    const hasSelectedClient = sipClientDrafts.some(
      (client) => client.draftKey === selectedSipClientId
    );

    if (!hasSelectedClient) {
      setSelectedSipClientId(sipClientDrafts[0].draftKey);
    }
  }, [selectedSipClientId, sipClientDrafts]);

  useEffect(() => {
    if (focusEmbeddedSipClientId && sipClientDrafts.some((client) => String(client._id || "") === String(focusEmbeddedSipClientId))) {
      setSelectedSipClientId(String(focusEmbeddedSipClientId));
    }
  }, [focusEmbeddedSipClientId, sipClientDrafts]);

  useEffect(() => {
    let active = true;

    const loadDirectory = () => Promise.all([fetchSipClientOptions(), fetchSipClientDirectory()])
      .then(([data, directoryData]) => {
        if (!active) return;
        const items = Array.isArray(data) ? data : data?.items || [];
        setSipClientDirectory(Array.isArray(directoryData?.items) ? directoryData.items : []);
        setSipClientOptions(
          items
            .map((item) => ({ _id: String(item?._id || ""), name: String(item?.name || "").trim() }))
            .filter((item) => item._id && item.name)
        );
      })
      .catch(() => {
        if (active) { setSipClientOptions([]); setSipClientDirectory([]); }
      });
    loadDirectory();
    window.addEventListener(SIP_CLIENT_DIRECTORY_CHANGED_EVENT, loadDirectory);

    return () => {
      active = false;
      window.removeEventListener(SIP_CLIENT_DIRECTORY_CHANGED_EVENT, loadDirectory);
    };
  }, []);

  const selectedCategory = formState.category || "residential";

  const visibleFields =
    FIELDS_BY_CATEGORY[selectedCategory] || FIELDS_BY_CATEGORY.residential;

  const handleChange = (event) => {
    const { name, type, value, checked } = event.target;

    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePricesChange = (nextPrices) => {
    setFormState((prev) => ({
      ...prev,
      prices: nextPrices,
    }));
  };

  const handleAddSipClient = () => {
    const option = sipClientOptions.find((item) => item._id === newSipClientId);
    const nextClientName = String(option?.name || "").trim();

    if (!nextClientName) {
      setError("Выберите клиента из справочника.");
      return;
    }

    const draft = createSipDraft(nextClientName, null, option._id);
    setSipClientDrafts((prev) => [...prev, draft]);
    setSelectedSipClientId(draft.draftKey);
    setNewSipClientId("");
    setIsAddSipClientOpen(false);
    setError("");
  };

  const handleSipDraftChange = (sipClientId, field, value) => {
    setSipClientDrafts((prev) =>
      prev.map((client) =>
        client.draftKey === sipClientId
          ? { ...client, [field]: value }
          : client
      )
    );
  };

  const buildSipPayload = (client) => ({
    ...(client?._id ? { _id: client._id } : {}),
    clientId: String(client?.clientId || ""),
    clientName: String(client?.clientName || "").trim(),
    status: Boolean(client?.status),
    sip_number: String(client?.sip_number ?? "").trim() || null,
    type: String(client?.type ?? "").trim() || null,
    transfer_number: transferNumberToPayload(client?.transfer_number),
    offerPrice: offerPriceToPayload(client?.offerPrice),
  });

  const handleSaveSelectedSipClient = async (client) => {
    if (!complex?._id) {
      setError("Сначала сохраните ЖК, затем сохраните клиента.");
      return;
    }

    setIsSipSaving(true);
    setError("");
    try {
      const existingSipClientId = String(client?._id || "") || null;
      const existingIds = new Set(sipClientDrafts.map((item) => String(item._id || "")).filter(Boolean));
      const saved = await saveSipClient(complex._id, existingSipClientId, buildSipPayload(client));
      const savedClient = existingSipClientId
        ? sipClientsToArray(saved?.sipClients).find((item) => String(item?._id || "") === existingSipClientId)
        : sipClientsToArray(saved?.sipClients).find((item) => !existingIds.has(String(item?._id || "")));

      if (!savedClient?._id) throw new Error("Сервер не вернул сохранённую строку клиента.");

      const nextDraft = { ...createSipDraft(savedClient.clientName, savedClient), draftKey: String(savedClient._id) };
      setSipClientDrafts((current) => current.map((item) => item.draftKey === client.draftKey ? nextDraft : item));
      setSelectedSipClientId(nextDraft.draftKey);
      setSavedSipDraftSignatures((current) => {
        const next = { ...current };
        if (nextDraft.draftKey !== client.draftKey) delete next[client.draftKey];
        next[nextDraft.draftKey] = sipDraftSignature(nextDraft);
        return next;
      });
      notify("Клиент сохранён", "success");
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : "Не удалось сохранить клиента.";
      setError(message);
      notify(message, "error");
    } finally {
      setIsSipSaving(false);
    }
  };

  const handleApplySipClient = async (client) => {
    if (!complex?._id) {
      setError("Сначала сохраните ЖК, затем запустите клиента.");
      return;
    }

    const clientName = String(client?.clientName || "").trim();
    const existingSipClientId = String(client?._id || "") || null;

    if (!clientName) {
      setError("Клиент обязателен.");
      return;
    }

    setIsSipSaving(true);
    setError("");

    try {
      const saved = await saveSipClient(
        complex._id,
        existingSipClientId,
        buildSipPayload(client)
      );
      const originalClient = sipClientsToArray(complex?.sipClients).find(
        (item) => String(item?._id || "") === existingSipClientId
      );
      const previousIds = new Set(sipClientsToArray(complex?.sipClients).map((item) => String(item?._id || "")));
      const savedClient = existingSipClientId
        ? sipClientsToArray(saved?.sipClients).find((item) => String(item?._id || "") === existingSipClientId)
        : sipClientsToArray(saved?.sipClients).find((item) => !previousIds.has(String(item?._id || "")));
      const savedSipClientId = String(savedClient?._id || existingSipClientId || "");
      const changedFields = [
        ["sip_number", String(originalClient?.sip_number ?? "").trim(), String(savedClient?.sip_number ?? "").trim()],
        ["transfer_number", Number(originalClient?.transfer_number), Number(savedClient?.transfer_number)],
        ["type", String(originalClient?.type ?? "").trim(), String(savedClient?.type ?? "").trim()],
        ["offerPrice", originalClient?.offerPrice ?? null, savedClient?.offerPrice ?? null],
        ["status", normalizeBoolean(originalClient?.status), normalizeBoolean(savedClient?.status)],
      ].filter(([, before, after]) => before !== after).map(([field]) => field);
      recordChanges(changedFields.length ? [{
        complexId: saved._id || complex._id,
        sipClientId: savedSipClientId,
        clientName,
        changedFields,
        snapshot: {
          complexName: saved.name || complex.name || "Без названия",
          sipNumber: savedClient?.sip_number ?? "",
          transferNumber: savedClient?.transfer_number ?? "",
          type: savedClient?.type || "",
          clientStatus: normalizeBoolean(savedClient?.status),
          currentSipClient: saved.currentSipClient || "",
          currentSipClientId: saved.currentSipClientId || "",
          complexStatus: saved.status,
        },
      }] : [], "single-edit");
      const applied = await applySipClient(saved._id || complex._id, savedSipClientId);

      setSelectedSipClientId(savedSipClientId);
      onSaved(applied, { close: false });
      notify("Клиент сохранён и запущен", "success");
    } catch (applyError) {
      const message = applyError instanceof Error
          ? applyError.message
          : "Не удалось запустить клиента.";
      setError(message); notify(message, "error");
    } finally {
      setIsSipSaving(false);
    }
  };

  const handleStopSipClient = async () => {
    if (!complex?._id) {
      setError("Сначала сохраните ЖК, затем остановите ЖК на карте.");
      return;
    }

    setIsSipSaving(true);
    setError("");

    try {
      const stopped = await stopCurrentSipClient(complex._id);
      onSaved(stopped, { close: false });
      notify("ЖК остановлен на карте", "success");
    } catch (stopError) {
      const message = stopError instanceof Error
          ? stopError.message
          : "Не удалось остановить ЖК на карте.";
      setError(message); notify(message, "error");
    } finally {
      setIsSipSaving(false);
    }
  };

  const handleSetSipClientStatus = async (client, status) => {
    const clientName = String(client?.clientName || "").trim();
    const sipClientId = String(client?._id || "");

    if (!complex?._id || !sipClientId || sipClientId.startsWith("new:")) {
      setError("Выберите клиента.");
      return;
    }

    setIsSipSaving(true);
    setError("");

    try {
      const updated = await setSipClientStatus(
        complex._id,
        sipClientId,
        status
      );
      setSelectedSipClientId(sipClientId);
      const previousStatus = normalizeBoolean(client?.status);
      if (previousStatus !== status) {
        const updatedClient = sipClientsToArray(updated?.sipClients).find(
          (item) => String(item?._id || "") === sipClientId
        );
        recordChanges([{
          complexId: updated._id || complex._id,
          sipClientId,
          clientName,
          changedFields: ["status"],
          snapshot: {
            complexName: updated.name || complex.name || "Без названия",
            sipNumber: updatedClient?.sip_number ?? "",
            transferNumber: updatedClient?.transfer_number ?? "",
            type: updatedClient?.type || "",
            clientStatus: normalizeBoolean(updatedClient?.status),
            currentSipClient: updated.currentSipClient || "",
            currentSipClientId: updated.currentSipClientId || "",
            complexStatus: updated.status,
          },
        }], "single-edit");
      }
      onSaved(updated, { close: false });
      notify(status ? "Клиент включён" : "Клиент остановлен", "success");
    } catch (statusError) {
      const message = statusError instanceof Error
          ? statusError.message
          : "Не удалось изменить статус клиента.";
      setError(message); notify(message, "error");
    } finally {
      setIsSipSaving(false);
    }
  };

  const handleDeleteSipClient = async (client) => {
    const sipClientId = String(client?._id || "");
    if (!window.confirm(`Удалить SIP-клиента ${client?.clientName || ""}?`)) return;
    if (!complex?._id || !sipClientId) {
      setSipClientDrafts((current) => {
        const removedIndex = current.findIndex((item) => item.draftKey === client.draftKey);
        const remaining = removeSipClientDraft(current, client.draftKey);
        if (selectedSipClientId === client.draftKey) {
          setSelectedSipClientId(remaining[removedIndex]?.draftKey || remaining[removedIndex - 1]?.draftKey || "");
        }
        return remaining;
      });
      return;
    }
    if (!complex?._id || !sipClientId) return;
    setIsSipSaving(true);
    setError("");
    try {
      const updated = await deleteSipClient(complex._id, sipClientId);
      if (selectedSipClientId === client.draftKey) {
        const removedIndex = sipClientDrafts.findIndex((item) => item.draftKey === client.draftKey);
        const remaining = sipClientDrafts.filter((item) => item.draftKey !== client.draftKey);
        setSelectedSipClientId(remaining[removedIndex]?.draftKey || remaining[removedIndex - 1]?.draftKey || "");
      }
      onSaved(updated, { close: false });
      notify("SIP-клиент удалён", "success");
    } catch (deleteError) {
      const message = deleteError instanceof Error ? deleteError.message : "Не удалось удалить SIP-клиента";
      setError(message);
      notify(message, "error");
    } finally {
      setIsSipSaving(false);
    }
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError("");

    try {
      const isCreateMode = !complex?._id;

      const basePayload = {
        status: isCreateMode
          ? normalizeBoolean(formState.status)
          : normalizeBoolean(complex?.status),
        name: formState.name.trim(),
        city: formState.city.trim(),
        category: selectedCategory,
        coordinates: coordinatesToPayload(formState.coordinates),
        ...(isCreateMode ? { sipClients: sipClientDrafts.map(buildSipPayload) } : {}),
        presetBase: String(formState.presetBase ?? "").trim(),
      };

      let payload = basePayload;

      if (selectedCategory === "residential") {
        payload = {
          ...basePayload,
          cityCategory: formState.cityCategory,
          developer: formState.developer,
          location: formState.location,
          metro: formState.metro,
          working_hours: formState.working_hours,
          isApartments: normalizeBoolean(formState.isApartments),
          completion_dates: textareaToArray(formState.completion_dates),
          finishes_available: textareaToArray(formState.finishes_available),
          prices: sectionsToPrices(formState.prices),
        };
      }

      if (selectedCategory === "commercial") {
        payload = {
          ...basePayload,
          cityCategory: formState.cityCategory,
          developer: formState.developer,
          location: formState.location,
          working_hours: formState.working_hours,
          info: formState.info,
          completion_dates: textareaToArray(formState.completion_dates),
        };
      }

      if (selectedCategory === "vehicles" || selectedCategory === "special") {
        payload = {
          ...basePayload,
          info: formState.info,
        };
      }

      const responseData = isCreateMode
        ? await createAdminComplex(payload)
        : await patchAdminComplex(complex._id, payload);

      const savedComplex = responseData?.item || responseData?.complex || responseData;

      onSaved(savedComplex || payload);
      notify(isCreateMode ? "ЖК добавлен" : "Изменения ЖК сохранены", "success");
      onClose();
    } catch (submitError) {
      const message = submitError instanceof Error
          ? submitError.message
          : "Не удалось сохранить изменения. Попробуйте ещё раз.";
      setError(message); notify(message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const isCreateMode = !complex?._id;
  const mainFields = visibleFields.filter((field) =>
    MAIN_FIELD_NAMES.has(field.name)
  );
  const detailFields = visibleFields.filter(
    (field) => !MAIN_FIELD_NAMES.has(field.name)
  );
  const currentSipClientId = String(complex?.currentSipClientId || "");
  const currentSipClientLabel =
    sipClientDrafts.find(
      (client) => String(client._id) === currentSipClientId
    )?.clientName || complex?.currentSipClient || "";
  const isComplexActive = normalizeBoolean(complex?.status);
  const clientOptions = sipClientOptions;
  const disabledDirectoryClientIds = new Set(sipClientDirectory.filter((item) => item?.isActive === false).map((item) => String(item._id)));
  const selectedSipClient =
    sipClientDrafts.find(
      (client) => client.draftKey === selectedSipClientId
    ) || sipClientDrafts[0] || null;
  const isSelectedSipClientDirty = selectedSipClient
    ? savedSipDraftSignatures[selectedSipClient.draftKey] !== sipDraftSignature(selectedSipClient)
    : false;

  const renderField = (field) => {
    const isWideField = WIDE_FIELD_NAMES.has(field.name) || field.type === "radio";
    const fieldClassName = isWideField
      ? "complex-edit-field complex-edit-field--wide"
      : "complex-edit-field";
    if (field.name === "city") {
      return (
        <label key={field.name} className={fieldClassName}>
          <span className="complex-edit-label">{field.label}</span>
          <select
            className="complex-edit-control"
            disabled={isSaving}
            name={field.name}
            onChange={handleChange}
            required
            value={formState.city}
          >
            <option value="">Выберите город</option>
            {cities.map((city) => <option key={city.key} value={city.key}>{city.name}</option>)}
          </select>
        </label>
      );
    }

    if (field.type === "checkbox") {
      return (
        <label key={field.name} className={fieldClassName}>
          <span className="complex-edit-label">{field.label}</span>
          <span className="complex-edit-check">
            <input
              className="complex-edit-checkbox"
              checked={Boolean(formState[field.name])}
              disabled={isSaving}
              name={field.name}
              onChange={handleChange}
              type="checkbox"
            />
            <span className="complex-edit-checkbox-label">
              {Boolean(formState[field.name]) ? "Да" : "Нет"}
            </span>
          </span>
        </label>
      );
    }

    if (field.type === "textarea") {
      return (
        <label key={field.name} className={fieldClassName}>
          <span className="complex-edit-label">{field.label}</span>
          <textarea
            className="complex-edit-control"
            disabled={isSaving}
            name={field.name}
            onChange={handleChange}
            rows={field.rows}
            value={formState[field.name]}
          />
          {field.hint ? <span className="complex-edit-hint">{field.hint}</span> : null}
        </label>
      );
    }

    if (field.type === "coordinates") {
      const coordinates = coordinatesToArray(formState.coordinates);

      return (
        <label key={field.name} className={fieldClassName}>
          <span className="complex-edit-label">{field.label}</span>
          <div className="complex-edit-coordinates">
            <input
              className="complex-edit-control"
              disabled={isSaving}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  coordinates: [
                    event.target.value,
                    coordinatesToArray(prev.coordinates)[1],
                  ],
                }))
              }
              placeholder="Широта"
              type="text"
              value={coordinates[0]}
            />
            <input
              className="complex-edit-control"
              disabled={isSaving}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  coordinates: [
                    coordinatesToArray(prev.coordinates)[0],
                    event.target.value,
                  ],
                }))
              }
              placeholder="Долгота"
              type="text"
              value={coordinates[1]}
            />
          </div>
        </label>
      );
    }

    if (field.type === "radio") {
      return (
        <label key={field.name} className={fieldClassName}>
          <span className="complex-edit-label">{field.label}</span>
          <div className="complex-edit-choice-grid">
            {field.options.map((option) => (
              <label
                className={cx(
                  "complex-edit-choice",
                  formState[field.name] === option.value && "complex-edit-choice--active",
                  isSaving && "complex-edit-choice--disabled"
                )}
                key={option.value}
              >
                <input
                  className="complex-edit-checkbox"
                  checked={formState[field.name] === option.value}
                  disabled={isSaving}
                  name={field.name}
                  onChange={handleChange}
                  type="radio"
                  value={option.value}
                />

                <span className="complex-edit-checkbox-label">{option.label}</span>
              </label>
            ))}
          </div>
        </label>
      );
    }

    return (
      <label key={field.name} className={fieldClassName}>
        <span className="complex-edit-label">{field.label}</span>
        <input
          className="complex-edit-control"
          disabled={isSaving}
          name={field.name}
          onChange={handleChange}
          required={field.name === "name"}
          type="text"
          value={formState[field.name]}
        />
      </label>
    );
  };

  return (
    <form className="complex-edit-form" onSubmit={handleSubmit}>
      <div className="complex-edit-form__content">
      <div className="complex-edit-section-header">
        <p className="complex-edit-hint">Изменения сохраняются сразу после отправки формы.</p>
      </div>

      {error ? <div className="complex-edit-error">{error}</div> : null}



      {(
        <section className="complex-edit-section">
          <div className="complex-edit-sip-body">
          {!isCreateMode && (
          <div
            className={cx(
              "complex-status-card",
              isComplexActive
                ? "complex-status-card--active"
                : "complex-status-card--stopped"
            )}
          >
            <div>
              <div className="complex-status-line">
                <span>ЖК на карте:</span>
                <span
                  className={cx(
                    "complex-edit-badge",
                    isComplexActive
                      ? "complex-status-badge--active"
                      : "complex-status-badge--stopped"
                  )}
                >
                  {isComplexActive ? "Активен" : "В стопе"}
                </span>
              </div>
              <div className="complex-status-line">
                <span>Клиент в работе:</span>
                <strong className="complex-status-current-client">{currentSipClientLabel || "—"}</strong>
              </div>
            </div>

            <button
              className="complex-edit-button complex-status-stop-button complex-edit-sip-action complex-edit-sip-action--danger"
              disabled={isSipSaving || !isComplexActive}
              onClick={handleStopSipClient}
              type="button"
            >
              Остановить ЖК полностью
            </button>
          </div>
          )}

          <div className="sip-client-tabs">
            {sipClientDrafts.map((client) => {
              const isClientActive = normalizeBoolean(client.status);
              const isCurrentClient = isComplexActive && currentSipClientId === String(client._id);
              const isSelectedClient = selectedSipClient && selectedSipClient.draftKey === client.draftKey;
              const isDisabledInDirectory = disabledDirectoryClientIds.has(String(client.clientId || ""));
              const tabClassName = cx(
                "sip-client-tab",
                isCurrentClient
                  ? "sip-client-tab--active-selected"
                  : isClientActive
                    ? "sip-client-tab--active"
                    : "sip-client-tab--stopped",
                isSelectedClient && "sip-client-tab--selected"
              );

              return (
                <button
                  className={tabClassName}
                  key={client.draftKey}
                  disabled={isSipSaving}
                  onClick={() => {
                    setSelectedSipClientId(client.draftKey);
                    setIsAddSipClientOpen(false);
                  }}
                  type="button"
                >
                  <span className="sip-client-tab-name">
                    {client.clientName} · {client.transfer_number || client.sip_number || "—"}
                  </span>
                  <span
                    className={cx(
                      "complex-edit-badge",
                      isClientActive
                        ? "sip-client-badge--active"
                        : "sip-client-badge--stopped"
                    )}
                  >
                    {isClientActive ? "Активен" : "В стопе"}
                  </span>
                  {isCurrentClient ? (
                    <span className="complex-edit-badge sip-client-badge--working-selected">
                      В работе
                    </span>
                  ) : null}
                  {isDisabledInDirectory ? <span className="complex-edit-badge sip-client-badge--directory-disabled">Отключён в справочнике</span> : null}
                </button>
              );
            })}

            <button
              className="sip-client-tab sip-client-tab--add"
              disabled={isSipSaving || clientOptions.length === 0}
              onClick={() => setIsAddSipClientOpen((prev) => !prev)}
              type="button"
            >
              + Добавить клиента
            </button>
          </div>

          {isAddSipClientOpen ? (
            <div className="sip-client-add-panel">
              <label className="complex-edit-field">
                <span className="complex-edit-label">Клиент из справочника</span>
                <select
                  className="complex-edit-control"
                  disabled={isSipSaving || clientOptions.length === 0}
                  onChange={(event) => setNewSipClientId(event.target.value)}
                  value={newSipClientId}
                >
                  <option value="">Выберите клиента</option>
                  {clientOptions.map((option) => (
                    <option key={option._id} value={option._id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </label>

              <button
                className="complex-edit-button complex-edit-button--primary complex-edit-button--sip"
                disabled={isSipSaving || !newSipClientId}
                onClick={handleAddSipClient}
                type="button"
              >
                Добавить клиента
              </button>
            </div>
          ) : null}

          {sipClientDrafts.length === 0 ? (
            <p className="complex-edit-hint">В этом ЖК пока нет клиентов.</p>
          ) : null}

          {selectedSipClient ? (() => {
            const isClientActive = normalizeBoolean(selectedSipClient.status);
            const isCurrentClient = isComplexActive && currentSipClientId === String(selectedSipClient._id);
            const isDisabledInDirectory = disabledDirectoryClientIds.has(String(selectedSipClient.clientId || ""));
            const cardClassName = cx(
              "sip-client-card",
              isCurrentClient
                ? "sip-client-card--active-selected"
                : isClientActive
                  ? "sip-client-card--active"
                  : "sip-client-card--stopped"
            );

            return (
              <article key={selectedSipClient.draftKey} className={cardClassName}>
                <div className="sip-client-card-header">
                  <div className="sip-client-card-heading">
                    <h4 className="sip-client-card-title">{selectedSipClient.clientName}</h4>
                    <div className="sip-client-badges">
                      <span className={cx(
                        "complex-edit-badge",
                        isCurrentClient
                          ? "sip-client-badge--working-selected"
                          : isClientActive
                            ? "sip-client-badge--working"
                            : "sip-client-badge--not-working"
                      )}>
                        Режим работы: {isCurrentClient ? "В работе" : "Не выбран"}
                      </span>
                      <span className={cx("complex-edit-badge", isClientActive ? "sip-client-badge--active" : "sip-client-badge--stopped")}>
                        Статус клиента: {isClientActive ? "Активен" : "В стопе"}
                      </span>
                      {isDisabledInDirectory ? <span className="complex-edit-badge sip-client-badge--directory-disabled">Отключён в справочнике</span> : null}
                    </div>
                  </div>
                </div>

                <div className="sip-client-card-grid">
                  {isCreateMode && (
                    <label className="complex-edit-field">
                      <span className="complex-edit-label">Статус клиента</span>
                      <select
                        className="complex-edit-control"
                        disabled={isSipSaving}
                        onChange={(event) => handleSipDraftChange(selectedSipClient.draftKey, "status", event.target.value === "true")}
                        value={String(isClientActive)}
                      >
                        <option value="true">Активен</option>
                        <option value="false">В стопе</option>
                      </select>
                    </label>
                  )}
                  <label className="complex-edit-field complex-edit-field--wide">
                    <span className="complex-edit-label">Тип перевода</span>
                    <textarea
                      className="complex-edit-control"
                      disabled={isSipSaving}
                      onChange={(event) => handleSipDraftChange(selectedSipClient.draftKey, "type", event.target.value)}
                      rows={3}
                      value={selectedSipClient.type}
                    />
                  </label>

                  <label className="complex-edit-field">
                    <span className="complex-edit-label">Номер перевода</span>
                    <input
                      className="complex-edit-control"
                      disabled={isSipSaving}
                      onChange={(event) => handleSipDraftChange(selectedSipClient.draftKey, "transfer_number", event.target.value)}
                      type="text"
                      value={selectedSipClient.transfer_number}
                    />
                  </label>

                  <label className="complex-edit-field">
                    <span className="complex-edit-label">СИП</span>
                    <input
                      className="complex-edit-control"
                      disabled={isSipSaving}
                      onChange={(event) => handleSipDraftChange(selectedSipClient.draftKey, "sip_number", event.target.value)}
                      type="text"
                      value={selectedSipClient.sip_number}
                    />
                  </label>

                  <label className="complex-edit-field">
                    <span className="complex-edit-label">Цена оффера, ₽</span>
                    <input className="complex-edit-control" disabled={isSipSaving} min="0" onChange={(event) => handleSipDraftChange(selectedSipClient.draftKey, "offerPrice", event.target.value)} placeholder="Например, 3500" step="any" type="number" value={selectedSipClient.offerPrice} />
                  </label>

                </div>

                <div className="sip-client-card-actions">
                  {!isCreateMode && <button
                    className="complex-edit-button complex-edit-button--primary complex-edit-button--sip"
                    disabled={isSipSaving || !isSelectedSipClientDirty || !complex?._id}
                    onClick={() => handleSaveSelectedSipClient(selectedSipClient)}
                    type="button"
                  >
                    {isSipSaving ? "Сохранение..." : "Сохранить клиента"}
                  </button>}
                  {!isCreateMode && !isCurrentClient && isClientActive ? (
                    <button
                      className="complex-edit-button complex-edit-button--primary complex-edit-button--sip complex-edit-sip-action complex-edit-sip-action--start"
                      disabled={isSipSaving}
                      onClick={() => handleApplySipClient(selectedSipClient)}
                      type="button"
                    >
                      Запустить клиента
                    </button>
                  ) : null}

                  {!isCreateMode && (!isClientActive ? (
                    <button
                      className="complex-edit-button complex-edit-button--sip complex-edit-sip-action complex-edit-sip-action--enable"
                      disabled={isSipSaving}
                      onClick={() => handleSetSipClientStatus(selectedSipClient, true)}
                      type="button"
                    >
                      Включить клиента
                    </button>
                  ) : (
                    <button
                      className="complex-edit-button complex-edit-button--sip complex-edit-sip-action complex-edit-sip-action--danger"
                      disabled={isSipSaving}
                      onClick={() => handleSetSipClientStatus(selectedSipClient, false)}
                      type="button"
                    >
                      Остановить клиента
                    </button>
                  ))}
                  <button
                    className="complex-edit-button complex-edit-button--sip complex-edit-sip-action complex-edit-sip-action--danger"
                    disabled={isSipSaving}
                    onClick={() => handleDeleteSipClient(selectedSipClient)}
                    type="button"
                  >
                    Удалить клиента
                  </button>
                </div>
              </article>
            );
          })() : null}
          </div>
        </section>
      )}

      <section className="complex-edit-section">
        <div className="complex-edit-section-header">
          <h3 className="complex-edit-header">Основные поля ЖК</h3>
        </div>

        <div className="complex-edit-grid">
          {mainFields.map(renderField)}
          {detailFields.map(renderField)}
        </div>
      </section>

      <section className="complex-edit-section">
        <div className="complex-edit-section-header">
          <h3 className="complex-edit-header">Тип объекта</h3>
        </div>

        <div className="complex-edit-grid">
          <label
            className="complex-edit-field complex-edit-field--wide"
          >
            <span className="complex-edit-label">Тип объекта</span>

            <div className="complex-edit-choice-grid">
              {CATEGORY_OPTIONS.map((option) => {
                const checked = formState.category === option.value;

                return (
                  <label
                    className={cx(
                      "complex-edit-choice",
                      checked && "complex-edit-choice--active",
                      isSaving && "complex-edit-choice--disabled"
                    )}
                    key={option.value}
                  >
                    <input
                      className="complex-edit-checkbox"
                      checked={checked}
                      disabled={isSaving}
                      name="category"
                      onChange={() =>
                        setFormState((prev) => ({
                          ...prev,
                          category: option.value,
                        }))
                      }
                      type="radio"
                      value={option.value}
                    />

                    <span className="complex-edit-checkbox-label">{option.label}</span>
                  </label>
                );
              })}
            </div>
          </label>
        </div>
      </section>

      {selectedCategory === "residential" && (
        <section className="complex-edit-section complex-edit-section--prices">
          <div className="complex-edit-section-header">
            <h3 className="complex-edit-header">Цены</h3>
            <p className="complex-edit-hint">Секции цен для жилого комплекса.</p>
          </div>

          <PricesEditor
            value={formState.prices}
            onChange={handlePricesChange}
            disabled={isSaving}
          />
        </section>
      )}

      </div>

      <div className="complex-edit-actions">
        <button
          className={cx("complex-edit-button", isSaving && "complex-edit-button--saving")}
          disabled={isSaving}
          onClick={onClose}
          type="button"
        >
          Отмена
        </button>
        <button
          className={cx(
            "complex-edit-button complex-edit-button--primary",
            isSaving && "complex-edit-button--saving"
          )}
          disabled={isSaving}
          type="submit"
        >
          {isSaving ? (isCreateMode ? "Создание..." : "Сохранение...") : isCreateMode ? "Добавить ЖК" : "Сохранить изменения"}
        </button>
      </div>
    </form>
  );
}

export default ComplexEditForm;
